import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Download,
  FileText,
  Grid2X2,
  ImageIcon,
  List,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCw,
  Search,
  StickyNote,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_TEXT_CHARS = 50_000;
const ACCEPTED_EXTENSIONS = [".txt", ".md", ".pdf", ".docx", ".png", ".jpg", ".jpeg", ".webp"];
const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "webp"]);

const isImageItem = (item: { file_name: string | null }) =>
  IMAGE_EXTENSIONS.has(item.file_name?.split(".").pop()?.toLowerCase() ?? "");

// Clipboard screenshots arrive as "image.png"; give them a findable name
// in the same style as a macOS screenshot.
const namePastedFile = (file: File, now = new Date()) => {
  if (file.name && file.name !== "image.png") return file;
  const pad = (value: number) => String(value).padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} at ${
    pad(now.getHours())}.${pad(now.getMinutes())}.${pad(now.getSeconds())}`;
  const extension = file.type === "image/jpeg" ? "jpg" : file.type.split("/")[1] || "png";
  return new File([file], `Pasted image ${stamp}.${extension}`, { type: file.type });
};

const isEditableTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

type ContactIntelItem = {
  id: string;
  item_type: "file" | "text";
  title: string;
  body_text: string | null;
  file_name: string | null;
  storage_path: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  extraction_status: "pending" | "done" | "failed";
  extraction_error: string | null;
  created_at: string;
  updated_at: string;
};

type Props = {
  businessId: string;
  customerId: string;
  contactId: string | null;
};

type LibraryFilter = "all" | "text" | "file";
type LibraryView = "grid" | "list";

const sanitizeFileName = (name: string) => name.replace(/[^a-zA-Z0-9._-]/g, "_");
const formatBytes = (bytes: number | null) => {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function ContactNotesCard({ businessId, customerId, contactId }: Props) {
  const queryClient = useQueryClient();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [savingText, setSavingText] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<LibraryFilter>("all");
  const [view, setView] = useState<LibraryView>("grid");
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ContactIntelItem | null>(null);
  const [textTitle, setTextTitle] = useState("");
  const [textBody, setTextBody] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);

  const {
    data: items = [],
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey: ["contact_notes", businessId, customerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_notes")
        .select("id, item_type, title, body_text, file_name, storage_path, mime_type, size_bytes, extraction_status, extraction_error, created_at, updated_at")
        .eq("business_id", businessId)
        .eq("customer_id", customerId)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ContactIntelItem[];
    },
    enabled: !!customerId,
  });

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((item) => {
      if (filter !== "all" && item.item_type !== filter) return false;
      if (!needle) return true;
      return [item.title, item.file_name, item.body_text]
        .some((value) => value?.toLowerCase().includes(needle));
    });
  }, [filter, items, search]);

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["contact_notes", businessId, customerId] }),
      queryClient.invalidateQueries({ queryKey: ["contact-knowledge-sources", businessId] }),
      queryClient.invalidateQueries({ queryKey: ["contact_projection", contactId] }),
    ]);
  };

  const invokeProcessor = async (itemId: string, action: "process" | "delete" = "process") => {
    const { data: sessionData } = await supabase.auth.getSession();
    const { data, error } = await supabase.functions.invoke("contact-notes-process", {
      body: { note_id: itemId, action },
      headers: sessionData.session
        ? { Authorization: `Bearer ${sessionData.session.access_token}` }
        : undefined,
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
  };

  const processItem = async (itemId: string) => {
    setProcessingId(itemId);
    try {
      await invokeProcessor(itemId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to process item");
    } finally {
      setProcessingId(null);
      await invalidate();
    }
  };

  const uploadFiles = async (files: File[]) => {
    if (!files.length) return;
    if (uploading) {
      toast.info("Wait for the current upload to finish, then add more files");
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > MAX_FILE_BYTES) {
          toast.error(`${file.name} is over the 10MB limit`);
          continue;
        }
        const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
        if (!ACCEPTED_EXTENSIONS.includes(extension)) {
          toast.error(`${file.name} is not a supported file type`);
          continue;
        }

        const path = `${businessId}/${crypto.randomUUID()}-${sanitizeFileName(file.name)}`;
        const { error: uploadError } = await supabase.storage
          .from("contact-notes")
          .upload(path, file);
        if (uploadError) throw uploadError;

        const { data: inserted, error: insertError } = await supabase
          .from("contact_notes")
          .insert({
            business_id: businessId,
            customer_id: customerId,
            contact_id: contactId,
            item_type: "file",
            title: file.name,
            storage_path: path,
            file_name: file.name,
            mime_type: file.type || null,
            size_bytes: file.size,
          })
          .select("id")
          .single();
        if (insertError) {
          await supabase.storage.from("contact-notes").remove([path]);
          throw insertError;
        }

        if (inserted?.id) void processItem(inserted.id);
      }
      await invalidate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    await uploadFiles(files);
  };

  // Drag files onto the library to add them. A depth counter keeps the
  // highlight steady while the pointer crosses child elements.
  const hasFiles = (event: React.DragEvent) => event.dataTransfer.types.includes("Files");
  const handleDragEnter = (event: React.DragEvent) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  };
  const handleDragOver = (event: React.DragEvent) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };
  const handleDragLeave = (event: React.DragEvent) => {
    if (!hasFiles(event)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragActive(false);
  };
  const handleDrop = (event: React.DragEvent) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    void uploadFiles(Array.from(event.dataTransfer.files));
  };

  // Paste (Cmd/Ctrl+V) files anywhere on the profile, except into a text
  // field or while a dialog is open, where paste keeps its usual job.
  const uploadFilesRef = useRef(uploadFiles);
  uploadFilesRef.current = uploadFiles;
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      if (isEditableTarget(event.target)) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      const files = Array.from(event.clipboardData?.files ?? []);
      if (!files.length) return;
      event.preventDefault();
      void uploadFilesRef.current(files.map((file) => namePastedFile(file)));
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);

  const openTextDialog = (item?: ContactIntelItem) => {
    setEditingItem(item ?? null);
    setTextTitle(item?.title ?? "");
    setTextBody(item?.body_text ?? "");
    setTextDialogOpen(true);
  };

  const saveTextItem = async (event: React.FormEvent) => {
    event.preventDefault();
    const title = textTitle.trim();
    const body = textBody.trim();
    if (!title || !body) return;
    setSavingText(true);
    try {
      let savedItemId: string;
      if (editingItem) {
        const { error } = await supabase
          .from("contact_notes")
          .update({ title, body_text: body })
          .eq("business_id", businessId)
          .eq("customer_id", customerId)
          .eq("id", editingItem.id);
        if (error) throw error;
        savedItemId = editingItem.id;
        toast.success("Note updated");
      } else {
        const { data, error } = await supabase
          .from("contact_notes")
          .insert({
            business_id: businessId,
            customer_id: customerId,
            contact_id: contactId,
            item_type: "text",
            title,
            body_text: body,
          })
          .select("id")
          .single();
        if (error) throw error;
        savedItemId = data.id;
        toast.success("Note added");
      }
      try {
        await invokeProcessor(savedItemId);
      } catch {
        toast.warning("The text was saved, but the AI profile refresh needs a retry");
      }
      setTextDialogOpen(false);
      setEditingItem(null);
      setTextTitle("");
      setTextBody("");
      await invalidate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save text");
    } finally {
      setSavingText(false);
    }
  };

  const openFile = async (item: ContactIntelItem, download = false) => {
    if (!item.storage_path) return;
    const previewWindow = download ? null : window.open("", "_blank");
    if (previewWindow) previewWindow.opener = null;
    try {
      const { data, error } = await supabase.storage
        .from("contact-notes")
        .createSignedUrl(item.storage_path, 60, download ? { download: true } : undefined);
      if (error) throw error;
      if (download) {
        const anchor = document.createElement("a");
        anchor.href = data.signedUrl;
        anchor.rel = "noopener noreferrer";
        anchor.click();
      } else if (previewWindow) {
        previewWindow.location.href = data.signedUrl;
      } else {
        const anchor = document.createElement("a");
        anchor.href = data.signedUrl;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.click();
      }
    } catch (error) {
      previewWindow?.close();
      toast.error(error instanceof Error ? error.message : "Could not open file");
    }
  };

  const deleteItem = async (item: ContactIntelItem) => {
    if (!window.confirm(`Delete “${item.title}”? This cannot be undone.`)) return;
    setDeletingId(item.id);
    try {
      await invokeProcessor(item.id, "delete");
      toast.success(item.item_type === "file" ? "File deleted" : "Note deleted");
      await invalidate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not delete item");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card
      className="relative border-border shadow-none"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragActive && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-foreground/30 bg-background/90"
        >
          <Upload className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium">Drop files to add them</p>
        </div>
      )}
      <CardHeader className="space-y-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="font-heading text-base font-semibold">Profile knowledge</CardTitle>
            <CardDescription className="text-sm">
              Notes and files that only apply to this contact. Drop or paste files to add them.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={uploadInputRef}
              id="contact-note-upload"
              type="file"
              multiple
              className="hidden"
              accept={ACCEPTED_EXTENSIONS.join(",")}
              onChange={handleUpload}
              disabled={uploading}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 px-2.5"
                  disabled={uploading}
                >
                  {uploading
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Plus className="h-3.5 w-3.5" />}
                  Add
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openTextDialog()}>
                  <StickyNote className="mr-2 h-4 w-4" />
                  Note
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => uploadInputRef.current?.click()}>
                  <Upload className="mr-2 h-4 w-4" />
                  File
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search notes and files..."
              className="h-8 pl-8"
            />
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-border p-1">
            {(["all", "text", "file"] as LibraryFilter[]).map((value) => (
              <button
                key={value}
                type="button"
                className={cn(
                  "inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors",
                  filter === value ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setFilter(value)}
                aria-pressed={filter === value}
              >
                {value === "file" ? "Files" : value === "text" ? "Notes" : value}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-border p-1">
            <button
              type="button"
              className={cn(
                "inline-flex items-center justify-center rounded-md p-1 text-xs font-medium transition-colors",
                view === "grid" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
              aria-label="Grid view"
              aria-pressed={view === "grid"}
              onClick={() => setView("grid")}
            >
              <Grid2X2 className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className={cn(
                "inline-flex items-center justify-center rounded-md p-1 text-xs font-medium transition-colors",
                view === "list" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
              aria-label="List view"
              aria-pressed={view === "list"}
              onClick={() => setView("list")}
            >
              <List className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : loadError ? (
          <p className="py-8 text-center text-sm text-destructive">Could not load notes and files.</p>
        ) : filteredItems.length === 0 ? (
          <div className="rounded-lg border border-dashed px-6 py-10 text-center">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm font-medium">
              {items.length === 0 ? "No notes or files yet" : "No matching notes or files"}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {items.length === 0
                ? "Add text, or drop, paste or upload a file to help the AI understand this contact. PDFs, Word documents, text files and images (.png, .jpg, .webp) all work."
                : "Try a different search or filter."}
            </p>
          </div>
        ) : (
          <div className={cn(
            view === "grid"
              ? "grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
              : "divide-y rounded-lg border",
          )}>
            {filteredItems.map((item) => (
              <IntelItem
                key={item.id}
                item={item}
                view={view}
                busy={processingId === item.id || deletingId === item.id}
                onOpen={() => item.item_type === "file" ? void openFile(item) : openTextDialog(item)}
                onEdit={() => openTextDialog(item)}
                onDownload={() => void openFile(item, true)}
                onRetry={() => void processItem(item.id)}
                onDelete={() => void deleteItem(item)}
              />
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={textDialogOpen} onOpenChange={(open) => {
        if (savingText) return;
        setTextDialogOpen(open);
        if (!open) setEditingItem(null);
      }}>
        <DialogContent className="admin-modal-content sm:max-w-2xl">
          <DialogHeader className="admin-modal-header">
            <DialogTitle>{editingItem ? "Edit note" : "Add note"}</DialogTitle>
            <DialogDescription>
              Add context your team and Dibs AI can use for this contact.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveTextItem} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="intel-text-title">Title</Label>
              <Input
                id="intel-text-title"
                value={textTitle}
                onChange={(event) => setTextTitle(event.target.value)}
                maxLength={200}
                placeholder="e.g. Discovery call notes"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="intel-text-body">Note</Label>
                <span className="text-xs text-muted-foreground">{textBody.length.toLocaleString()} / {MAX_TEXT_CHARS.toLocaleString()}</span>
              </div>
              <Textarea
                id="intel-text-body"
                value={textBody}
                onChange={(event) => setTextBody(event.target.value)}
                maxLength={MAX_TEXT_CHARS}
                placeholder="Type meeting notes, preferences, context, or other useful information..."
                className="min-h-56 resize-y"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={savingText} onClick={() => setTextDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingText || !textTitle.trim() || !textBody.trim()}>
                {savingText && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingItem ? "Save changes" : "Add note"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function IntelItem({
  item,
  view,
  busy,
  onOpen,
  onEdit,
  onDownload,
  onRetry,
  onDelete,
}: {
  item: ContactIntelItem;
  view: LibraryView;
  busy: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDownload: () => void;
  onRetry: () => void;
  onDelete: () => void;
}) {
  const isImage = item.item_type === "file" && isImageItem(item);
  const Icon = item.item_type === "text" ? StickyNote : isImage ? ImageIcon : FileText;
  const date = new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(item.updated_at));

  return (
    <div className={cn(
      "group flex min-w-0 bg-card",
      view === "grid"
        ? "min-h-44 flex-col rounded-lg border p-4"
        : "items-center gap-3 px-3 py-2.5",
    )}>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          "flex min-w-0 flex-1 text-left",
          view === "grid" ? "flex-col" : "items-center gap-3",
        )}
      >
        {view === "grid" && isImage && item.storage_path ? (
          <ImageThumbnail storagePath={item.storage_path} title={item.title} />
        ) : (
          <span className={cn(
            "flex shrink-0 items-center justify-center rounded-lg",
            item.item_type === "text" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700",
            view === "grid" ? "h-10 w-10" : "h-8 w-8",
          )}>
            <Icon className="h-4 w-4" />
          </span>
        )}
        <span className={cn("min-w-0", view === "grid" ? "mt-4" : "flex-1")}>
          <span className="block truncate text-sm font-medium" title={item.title}>{item.title}</span>
          {view === "grid" && item.item_type === "text" && (
            <span className="mt-1 block line-clamp-2 text-xs leading-5 text-muted-foreground">
              {item.body_text}
            </span>
          )}
          <span className={cn("block text-xs text-muted-foreground", view === "grid" ? "mt-3" : "mt-0.5")}>
            {date}{item.item_type === "file" ? ` · ${formatBytes(item.size_bytes)}` : ""}
          </span>
        </span>
      </button>

      <div className={cn("flex items-center gap-2", view === "grid" ? "mt-auto pt-3" : "shrink-0")}>
        <Badge variant="outline" className="h-5 px-1.5 text-[10px] capitalize">
          {item.item_type === "text" ? "note" : item.item_type}
        </Badge>
        {item.item_type === "file" && (
          <StatusIcon
            status={busy ? "pending" : item.extraction_status}
            error={item.extraction_error}
          />
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="ml-auto h-7 w-7" disabled={busy} aria-label={`Actions for ${item.title}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {item.item_type === "text" ? (
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            ) : (
              <>
                <DropdownMenuItem onClick={onOpen}>
                  <FileText className="mr-2 h-4 w-4" />
                  Open
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDownload}>
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </DropdownMenuItem>
                {item.extraction_status === "failed" && (
                  <DropdownMenuItem onClick={onRetry}>
                    <RotateCw className="mr-2 h-4 w-4" />
                    Retry processing
                  </DropdownMenuItem>
                )}
              </>
            )}
            <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function ImageThumbnail({ storagePath, title }: { storagePath: string; title: string }) {
  const [failed, setFailed] = useState(false);
  const { data: url } = useQuery({
    queryKey: ["contact-note-thumbnail", storagePath],
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from("contact-notes")
        .createSignedUrl(storagePath, 60 * 60);
      if (error) throw error;
      return data.signedUrl;
    },
    staleTime: 50 * 60 * 1000,
  });

  return (
    <span className="block h-24 w-full overflow-hidden rounded-md border bg-muted">
      {url && !failed ? (
        <img
          src={url}
          alt={title}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-muted-foreground">
          <ImageIcon className="h-5 w-5" />
        </span>
      )}
    </span>
  );
}

function StatusIcon({
  status,
  error,
}: {
  status: "pending" | "done" | "failed";
  error: string | null;
}) {
  if (status === "pending") {
    return <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />;
  }
  if (status === "done") {
    return <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-label="Processed" />;
  }
  return (
    <span title={error ?? "Extraction failed"}>
      <XCircle className="h-3.5 w-3.5 shrink-0 text-rose-600" aria-label="Processing failed" />
    </span>
  );
}
